import { ChangeDetectionStrategy, Component, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { AuthService, AvailableBase } from '../shared/services/apis/auth.service';

@Component({
    selector: 'app-login',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule],
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class LoginComponent {
    loginForm: FormGroup;
    errorMessage: string | null = null;
    isLoading = false;
    availableBases: AvailableBase[] = [];
    requiresBaseSelection = false;
    showPassword = false;
    // Locked after the first successful auth response to prevent autofill
    // from corrupting the password between the two-step base-selection flow
    private lockedPassword: string | null = null;

    constructor(
        private fb: FormBuilder,
        private router: Router,
        private authService: AuthService,
        private cdr: ChangeDetectorRef
    ) {
        this.loginForm = this.fb.group({
            identifier: ['', [Validators.required]],
            password: ['', Validators.required],
            baseAcessoId: ['']
        });
    }

    onSubmit() {
        if (this.loginForm.valid) {
            this.isLoading = true;
            this.errorMessage = null;

            const { identifier, baseAcessoId } = this.loginForm.value;

            // Always use the locked password if set — never re-read from the form
            // after the first step, since Android/iOS autofill can silently replace
            // the field value when the form changes shape (e.g. base selector appears)
            const password = this.lockedPassword ?? this.loginForm.value.password;

            const selectedBaseId = baseAcessoId ? Number(baseAcessoId) : undefined;

            this.authService.login(identifier, password, selectedBaseId).subscribe({
                next: (response) => {
                    this.isLoading = false;
                    if (response.success && response.requiresBaseSelection) {
                        const visibleBases = this.filterVisibleBases(response.availableBases);

                        if (visibleBases.length === 0) {
                            this.lockedPassword = null;
                            this.requiresBaseSelection = false;
                            this.availableBases = [];
                            this.loginForm.patchValue({ baseAcessoId: '' });
                            this.errorMessage = 'Nenhuma base de acesso disponível.';
                            this.cdr.markForCheck();
                            return;
                        }

                        // Lock the verified password so the base-selection step
                        // always sends exactly what the user typed, nothing else
                        this.lockedPassword = password;
                        this.requiresBaseSelection = true;
                        this.availableBases = visibleBases;
                        this.loginForm.patchValue({ baseAcessoId: '' });
                        this.errorMessage = response.message || 'Selecione a base de acesso.';
                    } else if (response.success && response.token && !response.approvalUserlOS) {
                        this.lockedPassword = null;
                        this.requiresBaseSelection = false;
                        this.availableBases = [];
                        this.router.navigate(['/home']);
                    } else if (response.success && response.token && response.approvalUserlOS) {
                        this.lockedPassword = null;
                        this.requiresBaseSelection = false;
                        this.availableBases = [];
                        this.router.navigate(['/os-approval']);
                    } else {
                        this.lockedPassword = null;
                        this.errorMessage = response.message || 'Falha no login.';
                    }
                    this.cdr.markForCheck();
                },
                error: (err) => {
                    this.isLoading = false;
                    this.lockedPassword = null;
                    if (err.error && err.error.message) {
                        this.errorMessage = err.error.message;
                    } else {
                        this.errorMessage = 'Erro ao conectar com o servidor.';
                    }
                    console.error('Login error:', err);
                    this.cdr.markForCheck();
                }
            });
        } else {
            Object.keys(this.loginForm.controls).forEach(key => {
                const control = this.loginForm.get(key);
                if (control) {
                    control.markAsTouched();
                }
            });
        }
    }

    private filterVisibleBases(bases: AvailableBase[] | undefined): AvailableBase[] {
        return (bases ?? []).filter(base => !base.descricao.toLowerCase().includes('[local]'));
    }

    get submitLabel(): string {
        if (this.isLoading) {
            return 'Entrando...';
        }

        return this.requiresBaseSelection ? 'Selecionar base' : 'Entrar';
    }
}
